import React, { useEffect, useRef, useState } from "react";
import {
  Container,
  Row,
  Col,
} from "react-bootstrap";
import { runData } from "../functions/api/processor";
import { useNavigate } from "react-router-dom";
import { useProcessing } from "../context/ProcessingModal";
import { useReport } from "../context/ReportModal";
import { useUser } from "../hooks/useUser";
import { Helmet } from "react-helmet";
import { initiateGoogleLoginFlow } from "../start/GoogleAuth";

import ProfileDetailsCard from "./components/ProfileDetailsCard";
import AccountSecurityCard from "./components/AccountSecurityCard";
import LinkedAccountsCard from "./components/LinkedAccountsCard";
import EditProfileModal from "./components/EditProfileModal";
import ChangePasswordModal from "./components/ChangePasswordModal";
import ChangeEmailModal from "./components/ChangeEmailModal";
import AvatarUploadModal from "./components/AvatarUploadModal";
import LoginHistoryCard from "./components/LoginHistoryCard"; // Import the new component

const UserProfile = () => {
  const { user, updateUser } = useUser();
  const [showEditModal, setShowEditModal] = useState(false);
  const [modalData, setModalData] = useState("");
  const [editField, setEditField] = useState("");
  const [editLabel, setEditLabel] = useState("");
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const [otpSuccessMessage, setOtpSuccessMessage] = useState(false);
  const { setProcessing } = useProcessing();
  const { setReport } = useReport();
  const [lastOtpRequest, setLastOtpRequest] = useState({
    type: null,
    time: null,
  });

  const navigate = useNavigate();
  const [otpRequested, setOtpRequested] = useState(false);
  const [otp, setOtp] = useState(Array(6).fill(""));

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [navigate]);

  // Add this log to check the user object content
  useEffect(() => {
    console.log("User object in UserProfile:", user);
  }, [user]);

  const handleOtpRequest = async (type) => {
    const TWO_MINUTES = 2 * 60 * 1000;
    const now = Date.now();

    if (
      lastOtpRequest?.type === type &&
      lastOtpRequest?.time &&
      now - lastOtpRequest.time < TWO_MINUTES
    ) {
      const remainingTime = Math.ceil(
        (TWO_MINUTES - (now - lastOtpRequest.time)) / 60000
      );
      setReport({
        show: true,
        message: `Please wait ${remainingTime} minute(s) before requesting a new OTP.`,
        type: "error",
      });
      return;
    }

    setOtp(Array(6).fill(""));
    setOtpSuccessMessage(false);

    if (type === "email") setShowEmailModal(false);
    if (type === "password") setShowPasswordModal(false);

    setProcessing(true);
    try {
      const response = await runData(
        { email: user.email, type: type },
        "/api/profile/request-otp/"
      );
      if (response.status === 200) {
        setOtpRequested(true);
        setOtpSuccessMessage(true);
        setLastOtpRequest({ type, time: now });
        if (type === "email") setShowEmailModal(true);
        if (type === "password") setShowPasswordModal(true);
      } else {
        setReport({
          show: true,
          message:
            response?.data?.message ||
            "An error occurred while requesting OTP.",
          type: "error",
        });
        if (type === "email") setShowEmailModal(true);
        if (type === "password") setShowPasswordModal(true);
      }
    } catch (err) {
      setReport({
        show: true,
        message:
          err.message || "An unexpected error occurred while requesting OTP.",
        type: "error",
      });
      if (type === "email") setShowEmailModal(true);
      if (type === "password") setShowPasswordModal(true);
    } finally {
      setProcessing(false);
    }
  };

  const validateInput = (input, field) => {
    switch (field) {
      case "email":
        return input;
      case "phone":
        return input.replace(/[^0-9+]/g, "");
      default:
        return input;
    }
  };

  const handleEditClick = (field, label) => {
    setEditField(field);
    setEditLabel(label);
    setModalData(user[field] || "");
    if (field === "email") {
      setOtpRequested(false);
      setOtp(Array(6).fill(""));
      setNewEmail("");
      setOtpSuccessMessage(false);
      setShowEmailModal(true);
    } else {
      setShowEditModal(true);
    }
  };

  const handleSave = async () => {
    const sanitizedData = validateInput(modalData, editField);
    setShowEditModal(false);
    setProcessing(true);
    try {
      const data = {
        [editField]: sanitizedData,
      };
      // Use PUT for update
      const response = await runData(data, "/api/profile/update/", "PUT"); // <-- Changed method to PUT

      if (response.status === 200) {
        updateUser({ [editField]: sanitizedData });
        setReport({
          show: true,
          message: `${editLabel} updated successfully.`,
          type: "success",
        });
      } else {
        setReport({
          show: true,
          message:
            response?.data?.message ||
            "An error occurred while updating the profile.",
          type: "error",
        });
      }
    } catch (err) {
      setReport({
        show: true,
        message:
          err.message ||
          "An unexpected error occurred while updating the profile.",
        type: "error",
      });
    } finally {
      setProcessing(false);
    }
  };

  const handlePasswordChange = () => {
    setOtpRequested(false);
    setOtp(Array(6).fill(""));
    setNewPassword("");
    setOtpSuccessMessage(false);
    setShowPasswordModal(true);
  };

  const handlePasswordSave = async () => {
    const joinedOtp = otp.join("").trim();
    if (joinedOtp.length === 6 && newPassword.length >= 8) {
      setShowPasswordModal(false);
      setProcessing(true);
      try {
        const data = {
          otp: joinedOtp,
          newPassword: newPassword,
        };
        // Use PUT for update-password
        const response = await runData(data, "/api/profile/update-password/", "PUT"); // <-- Changed method to PUT

        if (response.status === 200) {
          setReport({
            show: true,
            message: `Password updated successfully.`,
            type: "success",
          });
          setOtpRequested(false);
          setOtp(Array(6).fill(""));
          setNewPassword("");
          setOtpSuccessMessage(false);
        } else {
          setReport({
            show: true,
            message:
              response?.data?.message ||
              "An error occurred while changing the password.",
            type: "error",
          });
          setShowPasswordModal(true);
        }
      } catch (err) {
        setReport({
          show: true,
          message:
            err.message ||
            "An unexpected error occurred while changing the password.",
          type: "error",
        });
        setShowPasswordModal(true);
      } finally {
        setProcessing(false);
      }
    } else {
      setReport({
        show: true,
        message:
          "Please ensure OTP is filled and the new password is at least 8 characters.",
        type: "warning",
      });
      setShowPasswordModal(true);
    }
  };

  const handleEmailSave = async () => {
    const joinedOtp = otp.join("").trim();
    const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    if (joinedOtp.length === 6 && isValidEmail(newEmail)) {
      setShowEmailModal(false);
      setProcessing(true);
      try {
        const data = {
          otp: joinedOtp,
          email: user.email,
          newEmail: newEmail,
        };
        // Use PUT for update-email
        const response = await runData(data, "/api/profile/update-email/", "PUT"); // <-- Changed method to PUT

        if (response.status === 200) {
          updateUser({ email: newEmail });
          setReport({
            show: true,
            message: `Email update request successful. Please check ${newEmail} for verification.`,
            type: "success",
          });
          setOtpRequested(false);
          setOtp(Array(6).fill(""));
          setNewEmail("");
          setOtpSuccessMessage(false);
        } else {
          setReport({
            show: true,
            message:
              response?.data?.message ||
              "An error occurred while changing the email.",
            type: "error",
          });
          setShowEmailModal(true);
        }
      } catch (err) {
        setReport({
          show: true,
          message:
            err.message ||
            "An unexpected error occurred while changing the email.",
          type: "error",
        });
        setShowEmailModal(true);
      } finally {
        setProcessing(false);
      }
    } else {
      setReport({
        show: true,
        message: "Please ensure OTP is filled and the new email is valid.",
        type: "warning",
      });
      setShowEmailModal(true);
    }
  };

  const handleAvatarDrop = async (acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      if (file.size > 2 * 1024 * 1024) {
        setReport({
          show: true,
          message: "File size exceeds 2MB. Please select a smaller file.",
          type: "warning",
        });
        return;
      }
      setShowAvatarModal(false);
      setProcessing(true);
      try {
        const data = {
          avatar: file,
        };
        // Use PUT for update-avatar
        const response = await runData(data, "/api/profile/update-avatar/", "PUT"); // <-- Changed method to PUT

        if (response.status === 200) {
          updateUser({
            avatar: `${response.data.avatar}?t=${new Date().getTime()}`,
          });
          setReport({
            show: true,
            message: "Avatar uploaded successfully.",
            type: "success",
          });
        } else {
          setReport({
            show: true,
            message:
              response?.data?.message ||
              "An error occurred while uploading the avatar.",
            type: "error",
          });
        }
      } catch (err) {
        setReport({
          show: true,
          message:
            err.message ||
            "An unexpected error occurred while uploading the avatar.",
          type: "error",
        });
      } finally {
        setProcessing(false);
      }
    }
  };

  const handleKeyDown = (event) => {
    const activeElement = document.activeElement;
    const isTyping =
      activeElement &&
      (activeElement.tagName === "INPUT" ||
        activeElement.tagName === "TEXTAREA" ||
        activeElement.isContentEditable);
    if (isTyping) return;

    if (event.key === "Backspace") {
      event.preventDefault();
      const getTargetPath = () => "/";
      navigate(getTargetPath());
    }
  };

  const handleLinkGoogle = () => {
    const googleCallback = async (accessToken) => {
      setProcessing(true);
      try {
        const response = await runData(
          { token: accessToken },
          "/api/profile/link-google/"
        );
        if (response.status === 200) {
          updateUser({
            isGoogleLinked: true,
            googleLinkedEmail: response.data.linkedEmail || user.email,
          });
          setReport({
            show: true,
            message:
              response.data.message || "Google account linked successfully.",
            type: "success",
          });
        } else {
          setReport({
            show: true,
            message: response.data.message || "Failed to link Google account.",
            type: "error",
          });
        }
      } catch (err) {
        setReport({
          show: true,
          message:
            err.message || "An error occurred while linking Google account.",
          type: "error",
        });
      } finally {
        setProcessing(false);
      }
    };

    try {
      initiateGoogleLoginFlow(setProcessing, setReport, googleCallback);
    } catch (flowError) {
      setReport({
        show: true,
        message: flowError.message || "Failed to start Google linking process.",
        type: "error",
      });
      setProcessing(false);
    }
  };

  const handleUnlinkGoogle = async () => {
    setProcessing(true);
    try {
      // Use DELETE for unlink-google, no data payload needed usually
      const response = await runData({}, "/api/profile/unlink-google/", "DELETE"); // <-- Changed method to DELETE

      if (response.status === 200) {
        updateUser({ isGoogleLinked: false, googleLinkedEmail: null });
        setReport({
          show: true,
          message:
            response.data.message || "Google account unlinked successfully.",
          type: "success",
        });
      } else {
        setReport({
          show: true,
          message: response.data.message || "Failed to unlink Google account.",
          type: "error",
        });
      }
    } catch (err) {
      setReport({
        show: true,
        message:
          err.message || "An error occurred while unlinking Google account.",
        type: "error",
      });
    } finally {
      setProcessing(false);
    }
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setModalData("");
  };

  const closePasswordModal = () => {
    setShowPasswordModal(false);
    setOtpRequested(false);
    setOtp(Array(6).fill(""));
    setNewPassword("");
    setOtpSuccessMessage(false);
  };

  const closeEmailModal = () => {
    setShowEmailModal(false);
    setOtpRequested(false);
    setOtp(Array(6).fill(""));
    setNewEmail("");
    setOtpSuccessMessage(false);
  };

  const closeAvatarModal = () => {
    setShowAvatarModal(false);
  };

  return (
    <Container fluid className="py-4">
      <Helmet>
        <title>Profile Settings - Marmasco Technologies</title>
      </Helmet>

      <Row className="mb-4">
        <Col lg={8} className="mb-4 mb-lg-0">
          <ProfileDetailsCard
            user={user}
            onEditClick={handleEditClick}
            onAvatarClick={() => setShowAvatarModal(true)}
          />
          {/* Add the LoginHistoryCard below the ProfileDetailsCard */}
          <div className="mt-4">
             <LoginHistoryCard />
          </div>
        </Col>

        <Col lg={4}>
          <div style={{ position: "sticky", top: 20 }}>
            <AccountSecurityCard onPasswordChangeClick={handlePasswordChange} />

            <LinkedAccountsCard
              user={user}
              onLinkGoogle={handleLinkGoogle}
              onUnlinkGoogle={handleUnlinkGoogle}
            />
          </div>
        </Col>
      </Row>

      <EditProfileModal
        show={showEditModal}
        onHide={closeEditModal}
        onSave={handleSave}
        label={editLabel}
        value={modalData}
        onChange={setModalData}
        validateInput={validateInput}
        field={editField}
      />

      <ChangePasswordModal
        show={showPasswordModal}
        onHide={closePasswordModal}
        onSave={handlePasswordSave}
        onRequestOtp={handleOtpRequest}
        otp={otp}
        setOtp={setOtp}
        newPassword={newPassword}
        setNewPassword={setNewPassword}
        otpRequested={otpRequested}
        setOtpRequested={setOtpRequested}
        otpSuccessMessage={otpSuccessMessage}
      />

      <ChangeEmailModal
        show={showEmailModal}
        onHide={closeEmailModal}
        onSave={handleEmailSave}
        onRequestOtp={handleOtpRequest}
        otp={otp}
        setOtp={setOtp}
        newEmail={newEmail}
        setNewEmail={setNewEmail}
        otpRequested={otpRequested}
        setOtpRequested={setOtpRequested}
        otpSuccessMessage={otpSuccessMessage}
      />

      <AvatarUploadModal
        show={showAvatarModal}
        onHide={closeAvatarModal}
        onDrop={handleAvatarDrop}
      />
    </Container>
  );
};

export default UserProfile;
